import { FetchAPI } from '../Main/fetchAPI';
import { CLIENTS, CLIENT_USERS } from '../constants';
import {
	AssignUsersToClientProperties,
	ClientUserPaginationProperties,
	ClientUserProperties,
	SaveMeetAndGreetAuthorizationProperties,
	UpdateBillingProperties,
	UpdateDefaultBCBAProperties,
	UserClientPaginationProperties,
} from './userClientProperties';

export const UserClient = {
	users: {
		getAuthorizationInfoRBT: (userId: string) =>
			FetchAPI.getJson({
				urlStr: `${CLIENT_USERS}/${userId}/insurance-authorization/rbt-available-hours`,
			}),
		getBillableUsers: () =>
			FetchAPI.getJson({
				urlStr: `${CLIENT_USERS}/billable`,
			}),
		getSupervisors: () =>
			FetchAPI.getJson({
				urlStr: `${CLIENT_USERS}/supervisors`,
			}),
		getCaseCoordinators: () =>
			FetchAPI.getJson({
				urlStr: `${CLIENT_USERS}/case-coordinators`,
			}),
		getClients: (data: UserClientPaginationProperties) => {
			const { userId, page, pageSize, direction, orderBy, searchString, status, insuranceIds } = data;
			const query = new URLSearchParams();
			let ids = '';

			if (searchString) query.set('searchString', searchString);
			if (direction) query.set('direction', direction);
			if (orderBy) query.set('orderBy', orderBy);
			if (status) query.set('status', status);
			if (page) query.set('page', page);
			if (pageSize) query.set('pageSize', pageSize);

			if (insuranceIds?.length) {
				ids = insuranceIds.join('&insuranceIds=');
			}

			return FetchAPI.getJson({
				urlStr: `${CLIENT_USERS}/${userId}/patients?${query.toString()}${ids.length ? `&insuranceIds=${ids}` : ''}`,
			});
		},
		getAvailableClients: (data: UserClientPaginationProperties) => {
			const { userId, page, pageSize, direction, orderBy, searchString, status } = data;
			const query = new URLSearchParams();

			if (searchString) query.set('searchString', searchString);
			if (direction) query.set('direction', direction);
			if (orderBy) query.set('orderBy', orderBy);
			if (status) query.set('status', status);
			if (page) query.set('page', page);
			if (pageSize) query.set('pageSize', pageSize);

			return FetchAPI.getJson({
				urlStr: `${CLIENT_USERS}/${userId}/patients/available?${query.toString()}`,
			});
		},
		getBTs: (userId: string) =>
			FetchAPI.getJson({
				urlStr: `${CLIENT_USERS}/${userId}/rbts`,
			}),
	},
	client: {
		getBillingUserOfClient: (clientId: string) =>
			FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/billing-therapist`,
			}),
		getTherapists: ({ clientId, clinicallySupervisedById }: { clientId: string; clinicallySupervisedById?: string }) =>
			FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/therapists${
					clinicallySupervisedById ? `?clinicallySupervisedById=${clinicallySupervisedById}` : ''
				}`,
			}),
		getTherapistsDetails: ({ clientId, includeDeleted = false }: { clientId: string; includeDeleted?: boolean }) =>
			FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/therapists/details?includeDeleted=${includeDeleted}`,
			}),
		getBTs: (clientId: string) =>
			FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/rbts`,
			}),
		assignUsersToClient: (data: AssignUsersToClientProperties) =>
			FetchAPI.postJson({
				urlStr: `${CLIENTS}/${data.clientId}/therapists`,
				data,
			}),
		saveMeetAndGreetAuthorization: (data: SaveMeetAndGreetAuthorizationProperties) =>
			FetchAPI.postJson({
				urlStr: `${CLIENTS}/${data.clientId}/meet-and-greet/authorize`,
				data,
			}),
		getAvailableTherapists: (data: ClientUserPaginationProperties) => {
			const { clientId, page, pageSize, direction, orderBy, searchString, status } = data;
			const query = new URLSearchParams();

			if (searchString) query.set('searchString', searchString);
			if (direction) query.set('direction', direction);
			if (orderBy) query.set('orderBy', orderBy);
			if (status) query.set('status', status);
			if (page) query.set('page', page);
			if (pageSize) query.set('pageSize', pageSize);

			return FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/therapists/available?${query.toString()}`,
			});
		},
		getLicensedBCBAs: (data: ClientUserPaginationProperties) => {
			const { clientId, page, pageSize, searchString } = data;
			const query = new URLSearchParams();
			query.set('authorizationType', '1');

			if (searchString) query.set('searchString', searchString);
			if (page) query.set('page', page);
			if (pageSize) query.set('pageSize', pageSize);

			return FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/therapists/bcba/available?${query.toString()}`,
			});
		},
		getAvailableCredentialedBCBAs: (data: ClientUserPaginationProperties) => {
			const { clientId, page, pageSize, direction, orderBy, searchString, status, authorizationType, section } = data;
			const query = new URLSearchParams();
			query.set('credentialed', 'true');

			if (searchString) query.set('searchString', searchString);
			if (direction) query.set('direction', direction);
			if (orderBy) query.set('orderBy', orderBy);
			if (status) query.set('status', status);
			if (page) query.set('page', page);
			if (pageSize) query.set('pageSize', pageSize);
			if (authorizationType) query.set('authorizationType', authorizationType);
			if (section) query.set('section', section);

			return FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/therapists/bcba/available?${query.toString()}`,
			});
		},
		getAvailableNotCredentialedBCBAs: (data: ClientUserPaginationProperties) => {
			const { clientId, page, pageSize, direction, orderBy, searchString, status, authorizationType, section } = data;
			const query = new URLSearchParams();
			query.set('credentialed', 'false');

			if (searchString) query.set('searchString', searchString);
			if (direction) query.set('direction', direction);
			if (orderBy) query.set('orderBy', orderBy);
			if (status) query.set('status', status);
			if (page) query.set('page', page);
			if (pageSize) query.set('pageSize', pageSize);
			if (authorizationType) query.set('authorizationType', authorizationType);
			if (section) query.set('section', section);

			return FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/therapists/bcba/available?${query.toString()}`,
			});
		},
		getAvailableBTs: (data: ClientUserPaginationProperties) => {
			const { clientId, page, pageSize, direction, orderBy, searchString, status, authorizationType, section } = data;
			const query = new URLSearchParams();

			if (searchString) query.set('searchString', searchString);
			if (direction) query.set('direction', direction);
			if (orderBy) query.set('orderBy', orderBy);
			if (status) query.set('status', status);
			if (page) query.set('page', page);
			if (pageSize) query.set('pageSize', pageSize);
			if (authorizationType) query.set('authorizationType', authorizationType);
			if (section) query.set('section', section);

			return FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/therapists/rbt/available?${query.toString()}`,
			});
		},
		dissmissClient: (data: ClientUserProperties) =>
			FetchAPI.deleteJson({
				urlStr: `${CLIENTS}/${data.clientId}/therapists/${data.userId}`,
			}),
		getDefaultBCBAOptions: (clientId: string) =>
			FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/therapists/default-bcba/options`,
			}),
		getDefaultBCBA: (clientId: string) =>
			FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/therapists/default-bcba`,
			}),
		updateDefaultBCBA: (data: UpdateDefaultBCBAProperties) =>
			FetchAPI.postJson({
				urlStr: `${CLIENTS}/${data.clientId}/therapists/default-bcba`,
				data: data.data,
			}),
		getBillingStatus: ({ clientId, userId }: ClientUserProperties) =>
			FetchAPI.getJson({
				urlStr: `${CLIENTS}/${clientId}/therapists/${userId}/billing-stopped`,
			}),
		updateBilling: ({ clientId, userId, billingStopped }: UpdateBillingProperties) =>
			FetchAPI.putJson({
				urlStr: `${CLIENTS}/${clientId}/therapists/${userId}/billing-stopped`,
				data: { billingStopped },
			}),
	},
};
